/**
 * @fileoverview Payment Methods Management Hook
 * 
 * Provides hooks for managing organization payment methods through Stripe.
 * Handles fetching current payment method details and updating payment methods
 * via Stripe Checkout Sessions.
 * 
 * Payment methods are stored at the organization level and can be:
 * - Credit/debit cards
 * - US bank accounts (ACH)
 * 
 * The payment method update flow uses Stripe Checkout in 'setup' mode to
 * securely collect and verify payment information without charging the customer.
 * 
 * Key Features:
 * - Fetch organization's current payment method type and last 4 digits
 * - Initiate Stripe Checkout Session for updating payment method
 * - Automatic redirect to Stripe hosted page
 * - Webhook-based confirmation of new payment method
 * 
 * @module usePaymentMethods
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook for fetching an organization's payment method details
 * 
 * Retrieves the payment method information from the most recent payment
 * setup record. This includes the payment method type (card or bank account)
 * and the last 4 digits for display purposes.
 * 
 * @param organizationId - The UUID of the organization
 * @returns Query object with payment method data (stripe_customer_id, payment_method_type, payment_method_last4)
 * 
 * @example
 * ```tsx
 * function PaymentMethodDisplay({ organizationId }) {
 *   const { data: paymentMethod, isLoading } = usePaymentMethods(organizationId);
 * 
 *   if (isLoading) return <Spinner />;
 * 
 *   return (
 *     <div>
 *       <p>Payment Method: {paymentMethod?.payment_method_type || 'Not set'}</p>
 *       {paymentMethod?.payment_method_last4 && (
 *         <p>Last 4 digits: ****{paymentMethod.payment_method_last4}</p>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export const usePaymentMethods = (organizationId: string) => {
  return useQuery({
    queryKey: ['payment-methods', organizationId],
    queryFn: async () => {
      // Get organization's Stripe customer ID
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('stripe_customer_id')
        .eq('id', organizationId)
        .single();

      if (orgError) throw orgError;

      // Get payment method details from payment_setup_status (most recent)
      const { data: setupData } = await supabase
        .from('payment_setup_status')
        .select('payment_method_type, payment_method_last4')
        .eq('client_org_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      return {
        stripe_customer_id: orgData.stripe_customer_id,
        payment_method_type: setupData?.payment_method_type || null,
        payment_method_last4: setupData?.payment_method_last4 || null,
      };
    },
    enabled: !!organizationId,
  });
};

/**
 * Hook for updating an organization's payment method
 * 
 * Initiates the payment method update flow by creating a Stripe Checkout
 * Session in 'setup' mode. The user will be redirected to Stripe's hosted
 * page to securely enter their payment information.
 * 
 * After successful setup:
 * 1. User is redirected to successUrl
 * 2. Stripe webhook confirms the setup
 * 3. Payment method details are stored in payment_setup_status table
 * 4. Organization's default payment method is updated
 * 
 * Supported Payment Methods:
 * - Credit and debit cards (Visa, Mastercard, Amex, Discover)
 * - US bank accounts (ACH) - requires micro-deposit verification
 * 
 * @returns Mutation object for initiating payment method update
 * 
 * @example
 * ```tsx
 * function UpdatePaymentMethodButton({ organizationId }) {
 *   const updateMutation = useUpdatePaymentMethod();
 * 
 *   const handleUpdate = () => {
 *     updateMutation.mutate({
 *       organizationId,
 *       successUrl: `${window.location.origin}/settings/payment-complete`,
 *       cancelUrl: `${window.location.origin}/settings/payment-methods`
 *     });
 *     // User will be redirected to Stripe
 *   };
 * 
 *   return (
 *     <button onClick={handleUpdate} disabled={updateMutation.isPending}>
 *       {updateMutation.isPending ? 'Redirecting...' : 'Update Payment Method'}
 *     </button>
 *   );
 * }
 * ```
 */
export const useUpdatePaymentMethod = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      organizationId: string;
      successUrl: string;
      cancelUrl: string;
    }) => {
      const { data, error } = await invokeEdgeFunction('update-payment-method', {
        body: params,
        callerOperation: 'usePaymentMethods.updateMethod',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      const result = data as { sessionUrl?: string };
      if (result.sessionUrl) {
        window.location.href = result.sessionUrl;
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update payment method',
        variant: 'destructive',
      });
    },
  });
};
